import type { RankJobOption, RankMetaJob } from '@/domain/types'
import { jobIdentities, resolveJobIdentity } from './job-name'

export interface RankJobFamily {
  readonly name: string
  readonly jobKeys: readonly string[]
}

// CMS adventurer advancement paths. The first advancement has no chosen branch.
// 1–3: https://mxdc.dvg.cn/career_skill_guide.php; fourth names/IDs share job-name.ts.
export const rankJobFamilies: readonly RankJobFamily[] = [
  { name: '剑客系', jobKeys: ['110', '111', '112'] },
  { name: '准骑士系', jobKeys: ['120', '121', '122'] },
  { name: '枪战士系', jobKeys: ['130', '131', '132'] },
  { name: '火毒法师系', jobKeys: ['210', '211', '212'] },
  { name: '冰雷法师系', jobKeys: ['220', '221', '222'] },
  { name: '牧师系', jobKeys: ['230', '231', '232'] },
  { name: '猎人系', jobKeys: ['310', '311', '312'] },
  { name: '弩弓手系', jobKeys: ['320', '321', '322'] },
  { name: '刺客系', jobKeys: ['410', '411', '412'] },
  { name: '侠客系', jobKeys: ['420', '421', '422'] },
  { name: '拳手系', jobKeys: ['510', '511', '512'] },
  { name: '火枪手系', jobKeys: ['520', '521', '522'] },
]

const familyByKey = new Map(rankJobFamilies.flatMap(family => family.jobKeys.map(key => [key, family] as const)))
export const rankJobFamily = (key: string): RankJobFamily | undefined => familyByKey.get(key)

export interface RankJobFilterChoice {
  readonly key: string
  readonly name: string
  readonly aliases?: readonly string[]
}

export interface RankJobFamilyOption extends RankJobFilterChoice {
  readonly identityKey: string
  readonly family?: RankJobFamily
}

type JobMetadata = readonly Pick<RankMetaJob, 'key' | 'name'>[]
const jobKeyPattern = /^(?:[0-9]{1,4}|name-[a-f0-9]{16})$/

/** One stable representation for URLs, request coalescing, and caches. */
export function normalizeRankJobFilter(value: string | null | undefined): string | undefined {
  if (!value?.trim()) return undefined
  const keys = value.trim().split(',')
  if (keys.some(key => !jobKeyPattern.test(key))) return undefined
  return [...new Set(keys)].sort().join(',')
}

export function matchRankJobFamily(value: string | null | undefined): RankJobOption | null {
  const job = resolveJobIdentity(value)
  if (!job) return null
  const family = rankJobFamily(job.jobKey)
  return { jobKey: family?.jobKeys[0] ?? job.jobKey, jobName: family?.name ?? job.rankName }
}

/** Keep the upstream keys, including legacy hashes; names only establish lineage. */
export function rankJobFamilyOptions(jobs: JobMetadata): RankJobFamilyOption[] {
  const groups = new Map<string, { keys: string[]; option: RankJobFamilyOption; aliases: Set<string> }>()
  for (const job of jobs) {
    const identity = jobIdentities.find(item => item.jobKey === job.key) ?? resolveJobIdentity(job.name)
    const family = identity && rankJobFamily(identity.jobKey)
    const identityKey = family?.jobKeys[0] ?? identity?.jobKey ?? job.key
    const groupKey = family ? `family:${identityKey}` : job.key
    let group = groups.get(groupKey)
    if (!group) {
      group = { keys: [], option: { key: '', name: family?.name ?? job.name, identityKey, ...(family ? { family } : {}) }, aliases: new Set() }
      // Include the entire path, even advancements not yet present upstream.
      for (const member of family ? jobIdentities.filter(item => family.jobKeys.includes(item.jobKey)) : identity ? [identity] : []) {
        for (const alias of member.aliases) group.aliases.add(alias)
      }
      groups.set(groupKey, group)
    }
    group.keys.push(job.key)
    group.aliases.add(job.name)
  }
  return [...groups.values()].map(({ keys, option, aliases }) => ({ ...option,
    key: normalizeRankJobFilter(keys.join(','))!, aliases: [...aliases],
  }))
}

/** Expand old single-job links; explicit lists keep their supported members. */
export function resolveRankJobFilter(value: string | null | undefined, jobs: JobMetadata): string | undefined {
  const key = normalizeRankJobFilter(value)
  if (!key) return undefined
  if (key.includes(',')) {
    const supported = new Set(jobs.map(job => job.key))
    return normalizeRankJobFilter(key.split(',').filter(member => supported.has(member)).join(','))
  }
  return rankJobFamilyOptions(jobs).find(option => option.key.split(',').includes(key)
    || option.identityKey === key || option.family?.jobKeys.includes(key))?.key
}

export function rankJobFilterLabel(key: string | undefined, options: readonly RankJobFilterChoice[]): string | undefined {
  if (!key) return undefined
  return [...new Set(key.split(',').map(member => options.find(option => option.key.split(',').includes(member))?.name ?? member))].join(' / ')
}

export function rankJobFilterSelected(key: string | undefined, optionKey: string): boolean {
  return Boolean(key && key.split(',').every(member => optionKey.split(',').includes(member)))
}
